import type {ModalWindowResult, PayloadActionResponse} from 'typings/portaro.fe.types';
import type {ModalWindowSettings} from './modals.types';
import type {ui} from 'angular';
import type {SearchSelectionModalModel} from './search-selection-modal/types';
import type {UniversalFormModalModel} from './controllers/universal-form.controller';
import type {ActionResponse, ViewableFile} from 'typings/portaro.be.types';
import type {UserPreferencesModalModel} from './user-preferences-modal/types';
import type {SettingsModalModel} from './settings-modal/types';
import type {SpecialSymbolsKeyboardModalModel} from './special-symbols-keyboard-modal/types';
import type {DiagramNodeDetailsModalModel} from './diagram-node-details-modal/types';
import type {FilesSelectionModalModel} from './files-selection-modal/types';
import type {PasswordQuestionModalModel} from './password-question/types';
import type {ConfirmationDialogModalModel} from './confirmation-dialog/types';
import {isModalWindowResult} from 'shared/utils/types-utils';
import ModalWindow from './modal-window';
import type {UserFilesUploadModalModel} from 'src/modals/user-files-upload-modal/types';
import type {DiscountRequestModalModel} from 'src/modals/discount-request-modal/types';

export type ModalWindowType =
    'searchSelection' |
    'recordEditation' |
    'catalogueSelection' |
    'loanSelection' |
    'UniversalForm' |
    'registrationOptions' |
    'confirmationDialog' |
    'LoginForm' |
    'passwordQuestion' |
    'userPreferences' |
    'settings' |
    'field7Editor' |
    'field8Editor' |
    'authorityField8Editor' |
    'specialSymbolsKeyboard' |
    'diagramNodeDetails' |
    'filesSelection' |
    'userFilesUpload' |
    'discountRequest';

/**
 * @ngdoc service
 * @name modalConversationService
 * @module portaro.conversation
 *
 * @required $uibModal
 * @required constant:TEMPLATES_PATH
 *
 * @description
 * Service provides options to open and manage modal windows
 */
export default class ModalConversationService {
    public static readonly serviceName = 'modalConversationService';

    /*@ngInject*/
    constructor(private $uibModal: ui.bootstrap.IModalService, private TEMPLATES_PATH: string) {
    }

    /**
     * @ngdoc method
     * @name modalConversationService#openModalWindow
     *
     * @param {string} modalName name/type of the modal window
     * @param {any} modalModel data passed to modal window
     * @param {ModalWindowSettings} modalWindowSettings? optional settings for modal window
     *
     * @returns {Promise<ModalWindowResult>} promise with result of the modal window
     *
     * @description
     * Opens modal window and return promise with result of that window actions
     */
    openModalWindow<FORM_OBJECT>(modalName: 'UniversalForm', modalModel: UniversalFormModalModel, modalWindowSettings?: ModalWindowSettings): Promise<ModalWindowResult<PayloadActionResponse<FORM_OBJECT>>>;
    openModalWindow<RESULT extends ActionResponse>(modalName: ModalWindowType, modalModel, modalWindowSettings?: ModalWindowSettings): Promise<ModalWindowResult<RESULT>>;

    public async openModalWindow<RESULT extends ActionResponse>(modalName: ModalWindowType, modalModel?, modalWindowSettings?: ModalWindowSettings): Promise<ModalWindowResult<RESULT>> {
        const modalWindow = new ModalWindow(this.$uibModal, this.TEMPLATES_PATH);
        return modalWindow.open(modalName, modalModel, modalWindowSettings);
    }

    public async openModalWindowAndGetFinishedResponse<ACTION_RESPONSE extends ActionResponse>(modalName: ModalWindowType, modalModel?, modalWindowSettings?: ModalWindowSettings): Promise<ACTION_RESPONSE> {
        try {
            const modalWindowResult = await this.openModalWindow<ACTION_RESPONSE>(modalName, modalModel, modalWindowSettings);
            return modalWindowResult.finishedResponse;
        } catch (modalWindowResult) {
            if (isModalWindowResult(modalWindowResult)) {
                throw modalWindowResult.finishedResponse;
            }
            throw modalWindowResult;
        }
    }

    public async openModalWindowAndGetPayload<PAYLOAD>(modalName: ModalWindowType, modalModel, modalWindowSettings?: ModalWindowSettings): Promise<PAYLOAD> {
        const finishedResponse = await this.openModalWindowAndGetFinishedResponse<PayloadActionResponse<PAYLOAD>>(modalName, modalModel, modalWindowSettings);
        return finishedResponse.payload;
    }

    public async confirmationDialog<PAYLOAD>(modalModel: ConfirmationDialogModalModel): Promise<PAYLOAD> {
        return this.openModalWindowAndGetPayload<PAYLOAD>('confirmationDialog', modalModel, {isComponent: true});
    }

    public async passwordQuestion<PAYLOAD>(modalModel: PasswordQuestionModalModel): Promise<PAYLOAD> {
        return this.openModalWindowAndGetPayload<PAYLOAD>('passwordQuestion', modalModel, {isComponent: true});
    }

    public async userPreferencesModal<PAYLOAD>(modalModel: UserPreferencesModalModel): Promise<PAYLOAD> {
        return this.openModalWindowAndGetPayload<PAYLOAD>('userPreferences', modalModel, {isComponent: true});
    }

    public async settingsModal<PAYLOAD>(modalModel: SettingsModalModel): Promise<PAYLOAD> {
        return this.openModalWindowAndGetPayload<PAYLOAD>('settings', modalModel, {isComponent: true});
    }

    public async searchItem<PAYLOAD>(modalModel: SearchSelectionModalModel): Promise<PAYLOAD> {
        return this.openModalWindowAndGetPayload<PAYLOAD>('searchSelection', modalModel, {isComponent: true});
    }

    public async specialSymbolsKeyboard<PAYLOAD>(modalModel: SpecialSymbolsKeyboardModalModel): Promise<PAYLOAD> {
        return this.openModalWindowAndGetPayload<PAYLOAD>('specialSymbolsKeyboard', modalModel, {isComponent: true});
    }

    public async diagramNodeDetails<PAYLOAD>(modalModel: DiagramNodeDetailsModalModel): Promise<PAYLOAD> {
        return this.openModalWindowAndGetPayload<PAYLOAD>('diagramNodeDetails', modalModel, {isComponent: true});
    }

    public async filesSelection(modalModel: FilesSelectionModalModel): Promise<ViewableFile[]> {
        return this.openModalWindowAndGetPayload<ViewableFile[]>('filesSelection', modalModel, {isComponent: true});
    }

    public async userFilesUpload(modalModel: UserFilesUploadModalModel): Promise<string[]> {
        return this.openModalWindowAndGetPayload<string[]>('userFilesUpload', modalModel, {isComponent: true});
    }

    public async discountRequest(modalModel: DiscountRequestModalModel): Promise<void> {
        return this.openModalWindowAndGetPayload<void>('discountRequest', modalModel, {isComponent: true});
    }
}